import { useEffect, useState } from 'react'
import { Alert, Modal, Radio, Space, Spin, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { ORIGINAL_TEMPLATE_SENTINEL, listAutoProcessTemplates, type AutoProcessTemplate } from '../api/client'

/**
 * Shared "选择自动处理模板重新处理" dialog (spec 二/三/四/十五) — the single
 * component behind single-image "重跑全部流程", whole-group "全组重新生成",
 * and multi-select "批量重跑全部流程". All three read from the exact same
 * template list (`listAutoProcessTemplates`, the same data source the top
 * template bar uses) and funnel through the same `onConfirm(templateId)`
 * callback, which the caller wires to `rerunWithTemplate` /
 * `rerunGroupWithTemplate` / `batchRerunWithTemplate` — themselves thin
 * wrappers around the same backend rerun implementation, so the template
 * resolution logic can never drift between single/group/batch.
 *
 * There is deliberately no "模板快照" option here — only "原模板" (restore
 * this/these image(s)' own original template) and a flat list of every
 * other template, exactly per spec 十三.
 */
export default function RerunTemplateModal({
  open,
  title,
  originalOptionLabel,
  confirmText,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  /** "原模板（恢复原模板参数）" for a single image, "各自原模板" for batch/group. */
  originalOptionLabel: string
  confirmText?: string
  onCancel: () => void
  onConfirm: (templateId: string) => Promise<void>
}) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const [templates, setTemplates] = useState<AutoProcessTemplate[]>([])
  const [templateId, setTemplateId] = useState<string>(ORIGINAL_TEMPLATE_SENTINEL)
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    if (!open) return
    setTemplateId(ORIGINAL_TEMPLATE_SENTINEL)
    setLoading(true)
    listAutoProcessTemplates()
      .then(({ data }) => setTemplates(data.templates))
      .catch((e) => message.error((e as Error).message || t('rerunModal.loadFailed')))
      .finally(() => setLoading(false))
  }, [open, t])

  const handleOk = async () => {
    setConfirming(true)
    try {
      await onConfirm(templateId)
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={confirming ? undefined : onCancel}
      onOk={handleOk}
      okText={confirmText ?? t('rerunModal.confirmText')}
      cancelText={t('actions:cancel')}
      confirmLoading={confirming}
      destroyOnClose
      data-testid="rerun-template-modal"
    >
      <Spin spinning={loading}>
        {templates.length === 0 && !loading && <Alert type="warning" showIcon message={t('rerunModal.noTemplates')} />}
        <Radio.Group
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4, width: '100%' }}
        >
          <Radio value={ORIGINAL_TEMPLATE_SENTINEL} data-testid="rerun-template-option-original">
            {originalOptionLabel}
          </Radio>
          {templates.map((tpl) => (
            <Radio key={tpl.id} value={tpl.id} data-testid={`rerun-template-option-${tpl.id}`}>
              <Space size={6}>
                {tpl.name}
                {tpl.is_default && <span className="apt-chip-tag">{t('rerunModal.defaultTag')}</span>}
                {tpl.is_system && <span className="apt-chip-tag">{t('rerunModal.systemTag')}</span>}
              </Space>
            </Radio>
          ))}
        </Radio.Group>
      </Spin>
    </Modal>
  )
}
